package com.swetdot.swetgram.data.cache

import com.swetdot.swetgram.cache.SwetgramCache
import com.swetdot.swetgram.data.ioDispatcher
import kotlinx.coroutines.withContext

/**
 * Empties the local cache. Called when a session ends, by whichever route it ends.
 *
 * WHY THIS EXISTS. Everything in the cache was fetched by one account's session, but nothing in
 * the schema records whose it is -- the rows are keyed by conversation and user id, not by
 * viewer. So a sign-out that leaves them behind hands the next person to sign in the previous
 * user's inbox: their conversations, their message previews, their unread counts. On a shared
 * device that is a privacy failure rather than a stale-data annoyance.
 *
 * It is also incoherent, not merely stale. `Chat.peer(me)` and `Chat.displayTitle(me)` answer
 * "the other participant" with the first member who is not `me`; given rows whose member list
 * does not contain the new `me` at all, they return an arbitrary member. The observed result was
 * the signed-in user's OWN name and online dot rendered as a conversation, with an unread badge
 * that could never be cleared because the conversation was not theirs to read.
 *
 * Blocking work, so it hops to [ioDispatcher] -- sign-out is triggered from the main thread and
 * SQLite deletes are not free. Wrapped in one transaction so a failure part-way cannot leave
 * messages belonging to conversations that no longer exist.
 */
suspend fun SwetgramCache.clearAll() {
    withContext(ioDispatcher) {
        cacheQueries.transaction {
            cacheQueries.clearAllDailyTasks()
            cacheQueries.clearAllDiscoverFeeds()
            cacheQueries.clearAllMessages()
            cacheQueries.clearAllChatMembers()
            cacheQueries.clearAllChats()
            cacheQueries.clearAllUsers()
        }
    }
}
